import { Component, Inject, OnInit, OnDestroy, NgZone } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';

export interface CodeEditorDialogData {
  code: string;
  language: string;
  editorOptions?: any;
  title?: string;
}

@Component({
  selector: 'app-code-editor-dialog',
  templateUrl: './code-editor-dialog.component.html',
  styleUrls: ['./code-editor-dialog.component.scss']
})
export class CodeEditorDialogComponent implements OnInit, OnDestroy {
  public currentCode: string;
  public isDirty: boolean = false;
  public editor: any;
  public editorOptions: any;
  public isLoading: boolean = true;
  private themeObserver: MutationObserver;

  constructor(
    public dialogRef: MatDialogRef<CodeEditorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CodeEditorDialogData,
    private dialog: MatDialog,
    private ngZone: NgZone
  ) {
    this.currentCode = data.code || '';
  }

  ngOnInit(): void {
    // Configure editor options with theme detection
    const theme = this.isLightTheme() ? 'vs' : 'vs-dark';
    this.editorOptions = {
      theme,
      language: this.data.language || 'javascript',
      automaticLayout: true,
      fixedOverflowWidgets: true,
      ...this.data.editorOptions
    };

    // Watch for theme changes on body element
    this.setupThemeObserver();
  }

  ngOnDestroy(): void {
    // Clean up theme observer
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  /**
   * Detects if the body element has a light theme class
   */
  private isLightTheme(): boolean {
    const body = document.body;
    return body.classList.contains('light-theme') || body.classList.contains('theme-light');
  }

  /**
   * Updates the Monaco editor theme based on the current body theme
   */
  private updateTheme(): void {
    const theme = this.isLightTheme() ? 'vs' : 'vs-dark';

    // If editor is already initialized, update its theme
    if (this.editor) {
      this.ngZone.run(() => {
        (window as any).monaco?.editor.setTheme(theme);
      });
    }
  }

  /**
   * Sets up a MutationObserver to watch for theme changes on the body element
   */
  private setupThemeObserver(): void {
    this.themeObserver = new MutationObserver(() => {
      this.updateTheme();
    });

    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  onEditorInit(editor: any): void {
    this.editor = editor;
    this.isLoading = false;

    // Track changes for dirty state
    editor.onDidChangeModelContent(() => {
      const currentValue = editor.getValue();
      this.isDirty = currentValue !== this.data.code;
      this.currentCode = currentValue;
    });
  }

  save(): void {
    this.dialogRef.close(this.currentCode);
  }

  cancel(): void {
    if (this.isDirty) {
      // Show confirmation dialog for unsaved changes
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Unsaved Changes',
          message: 'You have unsaved changes. Are you sure you want to close without saving?',
          confirm: 'Close Without Saving',
          cancel: 'Continue Editing'
        }
      });

      confirmDialog.afterClosed().subscribe(result => {
        if (result) {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  close(): void {
    this.cancel();
  }
}